import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Check, RotateCcw, CalendarDays } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { InlineEmpty } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const WEEKDAYS_NL = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];
const WEEKDAYS_SHORT = ["ma", "di", "wo", "do", "vr", "za", "zo"];

// Type is carried by SHAPE, not colour: colour means machine condition only.
// solid = vast wekelijks · dashed = afspraak · dotted = onderhoud · double = keuring
const TYPE_STYLE = {
  vast_wekelijks: {
    mark: "bg-plaat",
    chip: "border-solid border-l-inkt bg-white",
  },
  eenmalig: {
    mark: "border border-dashed border-plaat",
    chip: "border-dashed border-l-inkt bg-white",
  },
  onderhoud: {
    mark: "border border-dotted border-plaat",
    chip: "border-dotted border-l-[3px] border-l-inkt bg-white",
  },
  keuring: {
    mark: "border-[3px] border-double border-plaat",
    chip: "border-double border-l-4 border-l-inkt bg-white",
  },
};
const TYPE_LABEL = { vast_wekelijks: "Vast wekelijks", eenmalig: "Afspraak", onderhoud: "Onderhoud" };

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function mondayOf(date) {
  const d = new Date(date);
  const offset = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - offset);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

const emptyForm = {
  title: "", description: "", type: "eenmalig", weekday: "0",
  due_date: toISO(new Date()), due_time: "09:00", assigned_to: "__none__",
  location_id: "__none__", asset_id: "__none__",
  reminder_enabled: false, reminder_offset: "24",
};

export default function Calendar() {
  const { canWrite, user, canView } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") === "month" ? "month" : "week");
  const [anchor, setAnchor] = useState(mondayOf(new Date()));
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const taskGuard = useUnsavedGuard(JSON.stringify(form) !== JSON.stringify(emptyForm), () => setOpen(false));
  const [inspections, setInspections] = useState([]);
  const [checklistInst, setChecklistInst] = useState(null);
  // Below sm the 7-column month grid is unreadable on a phone; fall back to the
  // week view (the one TD actually uses) and tell the user why.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const effectiveView = isMobile && view === "month" ? "week" : view;

  // Visible range depends on the view.
  const range = useMemo(() => {
    if (effectiveView === "week") {
      const start = mondayOf(anchor);
      return { start, end: addDays(start, 6) };
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: mondayOf(first), end: addDays(mondayOf(last), 6) };
  }, [effectiveView, anchor]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const reqs = [api.get(`/tasks/calendar?start=${toISO(range.start)}&end=${toISO(range.end)}`)];
      // §7: /inspections now lives behind the `keuringen` module. Fetching it
      // unguarded 403->redirects a user with Kalender visible but Keuringen hidden.
      if (canView("keuringen")) reqs.push(api.get("/inspections"));
      const [cal, insp] = await Promise.all(reqs);
      setInstances(cal.data);
      setInspections(insp ? insp.data : []);
    } catch (e) { setError(getErrorMessage(e, "Kan agenda niet laden.")); }
    finally { setLoading(false); }
  }, [range, canView]);

  useEffect(() => {
    (async () => {
      try {
        const [loc, ass, asg] = await Promise.all([
          api.get("/locations"), api.get("/assets"), api.get("/users/assignees"),
        ]);
        setLocations(loc.data); setAssets(ass.data); setAssignees(asg.data);
      } catch (e) { /* refs optional */ }
    })();
  }, []);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const m = {};
    for (const i of instances) { (m[i.date] = m[i.date] || []).push(i); }
    // Inspections appear on their next_due date, in their own colour.
    const startIso = toISO(range.start);
    const endIso = toISO(range.end);
    for (const ins of inspections) {
      if (!ins.next_due || ins.next_due < startIso || ins.next_due > endIso) continue;
      (m[ins.next_due] = m[ins.next_due] || []).push({
        task_id: `insp-${ins.id}`, title: `Keuring: ${ins.name}`, type: "keuring",
        date: ins.next_due, status: "open", inspection_status: ins.status,
      });
    }
    return m;
  }, [instances, inspections, range]);

  const onChipClick = (inst) => {
    if (inst.type === "keuring" || !canWrite()) return;
    // Onderhoud tasks with a checklist open the checklist before completion.
    if (inst.status !== "gedaan" && inst.type === "onderhoud" && inst.checklist?.length) {
      setChecklistInst(inst);
      return;
    }
    toggle(inst);
  };

  const toggle = async (inst) => {
    if (!canWrite()) return;
    try {
      if (inst.status === "gedaan") {
        await api.post(`/tasks/${inst.task_id}/uncomplete`, { date: inst.date });
      } else {
        await api.post(`/tasks/${inst.task_id}/complete`, { date: inst.date, status: "gedaan" });
      }
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Bijwerken mislukt.")); }
  };

  const shift = (dir) => {
    setAnchor((a) => (effectiveView === "week"
      ? addDays(a, dir * 7)
      : new Date(a.getFullYear(), a.getMonth() + dir, 1)));
  };

  const formLocation = locations.find((l) => l.location_id === form.location_id);
  const formAssets = assets.filter((a) => a.location_id === form.location_id);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titel is verplicht."); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, type: form.type,
        assigned_to: form.assigned_to === "__none__" ? null : form.assigned_to,
        location_id: form.location_id === "__none__" ? null : form.location_id,
        asset_id: form.asset_id === "__none__" ? null : form.asset_id,
        reminder: { enabled: form.reminder_enabled, offset_hours: Number(form.reminder_offset) || 24 },
      };
      if (form.type === "vast_wekelijks") payload.weekday = Number(form.weekday);
      else { payload.due_date = form.due_date; payload.due_time = form.due_time; }
      await api.post("/tasks", payload);
      toast.success("Toegevoegd aan de agenda");
      setOpen(false); setForm(emptyForm);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSaving(false); }
  };

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(range.start, i)), [range]);
  const monthWeeks = useMemo(() => {
    const weeks = [];
    let cur = new Date(range.start);
    while (cur <= range.end) {
      weeks.push(Array.from({ length: 7 }, (_, i) => addDays(cur, i)));
      cur = addDays(cur, 7);
    }
    return weeks;
  }, [range]);

  const todayISO = toISO(new Date());
  const rangeLabel = effectiveView === "week"
    ? `Week van ${range.start.toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}`
    : anchor.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });

  // "Mijn week" list: current visible week's instances (assigned to me first).
  const mijnWeek = useMemo(() => {
    const items = [...instances].sort((a, b) => a.date.localeCompare(b.date));
    const mine = items.filter((i) => i.assigned_to === user?.id);
    return (mine.length ? mine : items);
  }, [instances, user]);

  const consoleFacts = useMemo(() => {
    const total = instances.length;
    const done = instances.filter((i) => i.status === "gedaan").length;
    const late = instances.filter((i) => i.status !== "gedaan" && i.date < toISO(new Date())).length;
    return [rangeLabel, `${total} items`,
      ...(late ? [`${late} achterstallig`] : [`${done} afgehandeld`])];
  }, [instances, view, rangeLabel]);

  const consoleActions = canWrite()
    ? [{ key: "toevoegen", label: "Toevoegen", icon: Plus, variant: "primary",
         testid: "add-task-button", onClick: () => { setForm(emptyForm); setOpen(true); } }]
    : [];

  return (
    <Page console={{ variant: "view", title: "Kalender", facts: consoleFacts, actions: consoleActions }}>
      <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Tabs value={view} onValueChange={(v) => { if (v === "punten") { navigate("/mijn-werk"); } else { setView(v); } }}>
            <TabsList>
              <TabsTrigger value="week" data-testid="view-week">Week</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Maand</TabsTrigger>
              {canView("punten") && <TabsTrigger value="punten" data-testid="view-punten">Punten</TabsTrigger>}
            </TabsList>
          </Tabs>

        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => shift(-1)}><ChevronLeft size={18} /></Button>
          <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => setAnchor(mondayOf(new Date()))}>Vandaag</Button>
          <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => shift(1)}><ChevronRight size={18} /></Button>
          <span className="ml-2 text-body font-medium text-foreground" data-testid="cal-range-label">{rangeLabel}</span>
        </div>
        <Legend />
      </div>

      {error && <Card className="p-5"><p className="text-body text-destructive">{error}</p></Card>}

      {isMobile && view === "month" && (
        <Card className="border-wacht-rand bg-wacht-vlak p-3 text-data text-wacht-tekst" data-testid="calendar-mobile-hint">
          Maandweergave is te klein op een telefoon — hier zie je de week.
        </Card>
      )}

      {/* The week gets the full width: with a side panel each day column was ~100px
          and every chip read "Wekelijks…". Mijn week sits underneath. */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          {loading ? (
            <Card className="p-8 text-center text-body text-muted-foreground">Laden...</Card>
          ) : effectiveView === "week" ? (
            <>
              {/* Seven real columns with real height: a week keeps the shape of a
                  week, also when a day is empty. */}
              <div className="hidden grid-cols-7 gap-2 sm:grid" data-testid="calendar-week">
                {weekDays.map((d, i) => {
                  const iso = toISO(d);
                  const items = byDate[iso] || [];
                  const today = iso === todayISO;
                  return (
                    <Card key={iso} data-testid={`calendar-day-${iso}`} className={`flex min-h-[220px] flex-col p-2 ${today ? "border-borstel" : ""}`}>
                      <div className="mb-2 flex items-baseline justify-between border-b border-lijn-zacht pb-1">
                        <span className="text-data font-semibold text-foreground">
                          {WEEKDAYS_SHORT[i]} <span className="text-muted-foreground">{d.getDate()}</span>
                        </span>
                        {today && <span className="text-label text-borstel">vandaag</span>}
                      </div>
                      <div className="flex-1 space-y-1">
                        {items.map((inst) => <InstanceChip key={inst.task_id + inst.date} inst={inst} onToggle={onChipClick} canWrite={canWrite()} />)}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Phone: one row per day. Empty days stay visible but collapse to a
                  single line, so you can still see that nothing is planned. */}
              <div className="space-y-2 sm:hidden" data-testid="calendar-week-mobile">
                {weekDays.map((d, i) => {
                  const iso = toISO(d);
                  const items = byDate[iso] || [];
                  const today = iso === todayISO;
                  return (
                    <Card key={iso} data-testid={`calendar-day-mobile-${iso}`} className={`p-3 ${today ? "border-borstel" : ""} ${items.length === 0 ? "py-2" : ""}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="text-data font-semibold text-foreground">{WEEKDAYS_NL[i]} {d.getDate()}</span>
                        {today
                          ? <span className="text-label text-borstel">vandaag</span>
                          : items.length === 0 && <span className="text-label text-muted-foreground">niets gepland</span>}
                      </div>
                      {items.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {items.map((inst) => <InstanceChip key={inst.task_id + inst.date} inst={inst} onToggle={onChipClick} canWrite={canWrite()} />)}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </>
          ) : (
            <Card className="p-3" data-testid="calendar-month">
              <div className="mb-1 grid grid-cols-7 gap-1 text-center text-data font-semibold text-muted-foreground">
                {WEEKDAYS_SHORT.map((w) => <div key={w}>{w}</div>)}
              </div>
              {monthWeeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-1">
                  {week.map((d) => {
                    const iso = toISO(d);
                    const items = byDate[iso] || [];
                    const inMonth = d.getMonth() === anchor.getMonth();
                    return (
                      <div key={iso} className={`min-h-[84px] rounded-md border border-border p-1 ${inMonth ? "" : "opacity-40"} ${iso === todayISO ? "border-borstel" : ""}`}>
                        <div className="flex items-baseline justify-between">
                          <span className="text-label text-muted-foreground">{d.getDate()}</span>
                          {iso === todayISO && <span className="text-label text-borstel">vandaag</span>}
                        </div>
                        <div className="space-y-0.5">
                          {items.slice(0, 3).map((inst) => <InstanceChip key={inst.task_id + inst.date} inst={inst} onToggle={onChipClick} canWrite={canWrite()} compact />)}
                          {items.length > 3 && <div className="text-label text-muted-foreground">+{items.length - 3} meer</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Mijn week list */}
        <Card className="p-4" data-testid="mijn-week">
          <h2 className="mb-3 text-body font-semibold text-foreground">Mijn week</h2>
          {mijnWeek.length === 0 ? (
            <InlineEmpty
              testid="mijn-week-empty"
              title="Niets gepland deze week"
              purpose="Hier staan de taken en afspraken van deze week op een rij, zodat je ze kunt afvinken zonder de kalender af te zoeken."
              action={canWrite() ? { label: "Taak toevoegen", icon: Plus, onClick: () => { setForm(emptyForm); setOpen(true); } } : null}
            />
          ) : (
            <ul className="space-y-2">
              {mijnWeek.map((inst) => (
                <li key={inst.task_id + inst.date} className="flex items-start gap-2">
                  {canWrite() && (
                    <button data-testid={`mijnweek-toggle-${inst.task_id}`} onClick={() => onChipClick(inst)} className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${inst.status === "gedaan" ? "border-goed-rand bg-goed-rand text-white" : "border-input"}`}>
                      {inst.status === "gedaan" && <Check size={14} />}
                    </button>
                  )}
                  <div className="min-w-0">
                    <div className={`truncate text-body ${inst.status === "gedaan" ? "text-muted-foreground line-through" : "text-foreground"}`}>{inst.title}</div>
                    <div className="flex items-center gap-1 text-data text-muted-foreground">
                      <span className={`inline-block h-2 w-2 ${TYPE_STYLE[inst.type]?.mark}`} />
                      {new Date(inst.date).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric" })}{inst.due_time ? ` · ${inst.due_time}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Onderhoud checklist dialog (shown when ticking off a maintenance task) */}
      <Dialog open={!!checklistInst} onOpenChange={(o) => !o && setChecklistInst(null)}>
        <DialogContent data-testid="checklist-dialog">
          <DialogHeader><DialogTitle>{checklistInst?.title}</DialogTitle></DialogHeader>
          <DialogDescription>Werk de stappen af en markeer daarna als gedaan.</DialogDescription>
          <ol className="mt-2 space-y-2">
            {(checklistInst?.checklist || []).map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-body">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-data font-medium">{i + 1}</span>
                <span className="text-foreground">{step}</span>
              </li>
            ))}
          </ol>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setChecklistInst(null)}>Sluiten</Button>
            <Button data-testid="checklist-complete-button" onClick={async () => { const inst = checklistInst; setChecklistInst(null); await toggle(inst); }}>
              <Check size={18} className="mr-2" /> Markeer als gedaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add task / appointment dialog */}
      <Dialog open={open} onOpenChange={taskGuard.requestClose}>
        <DialogContent className="max-w-lg" srDescription="Zet een taak of afspraak in de agenda.">
          <DiscardDialog guard={taskGuard} />
          <DialogHeader><DialogTitle>Toevoegen aan agenda</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vast_wekelijks">Vast wekelijks</SelectItem>
                  <SelectItem value="eenmalig">Afspraak (eenmalig)</SelectItem>
                  <SelectItem value="onderhoud">Onderhoud</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Titel *</Label><Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Omschrijving</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>

            {form.type === "vast_wekelijks" ? (
              <div className="space-y-1.5">
                <Label>Weekdag</Label>
                <Select value={form.weekday} onValueChange={(v) => setForm((f) => ({ ...f, weekday: v }))}>
                  <SelectTrigger data-testid="task-weekday-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS_NL.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Datum</Label><Input type="date" data-testid="task-date-input" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Tijd</Label><Input type="time" value={form.due_time} onChange={(e) => setForm((f) => ({ ...f, due_time: e.target.value }))} /></div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Toewijzen aan</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Niemand" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Niemand</SelectItem>{assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Locatie (optioneel)</Label>
                <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v, asset_id: "__none__" }))}>
                  <SelectTrigger><SelectValue placeholder="Geen" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Geen</SelectItem>{locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {form.location_id !== "__none__" && (
              <div className="space-y-1.5">
                <Label>Asset (optioneel)</Label>
                <Select value={form.asset_id} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Geen" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Geen</SelectItem>{formAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_code} — {a.brand} {a.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label className="cursor-pointer">Herinnering</Label>
                <p className="text-data text-muted-foreground">E-mail vooraf (bij afspraken)</p>
              </div>
              <Switch data-testid="task-reminder-switch" checked={form.reminder_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, reminder_enabled: v }))} />
            </div>
            {form.reminder_enabled && (
              <div className="space-y-1.5">
                <Label>Uren vooraf</Label>
                <Input type="number" min="1" data-testid="task-reminder-offset" value={form.reminder_offset} onChange={(e) => setForm((f) => ({ ...f, reminder_offset: e.target.value }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => taskGuard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="task-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Page>
  );
}

const InstanceChip = ({ inst, onToggle, canWrite, compact }) => {
  const style = TYPE_STYLE[inst.type] || TYPE_STYLE.eenmalig;
  const done = inst.status === "gedaan";
  const isKeuring = inst.type === "keuring";
  const clickable = canWrite && !isKeuring;
  return (
    <button
      type="button"
      data-testid={`task-chip-${inst.task_id}`}
      onClick={() => clickable && onToggle(inst)}
      className={`flex w-full items-start gap-1 rounded border-l-2 px-1.5 py-1 text-left text-label ${style.chip} ${done ? "opacity-60" : ""} ${clickable ? "cursor-pointer" : "cursor-default"}`}
      title={inst.title}
    >
      {clickable && (done ? <RotateCcw size={14} className="mt-0.5 shrink-0" /> : <span className="mt-0.5 h-3 w-3 shrink-0 rounded-sm border border-current" />)}
      <span className={`line-clamp-2 ${done ? "line-through" : ""} ${compact ? "" : "font-medium"}`}>
        {inst.due_time ? `${inst.due_time} ` : ""}{inst.title}
      </span>
    </button>
  );
};

const Legend = ({ showKeuring }) => (
  <div className="hidden items-center gap-3 text-data text-muted-foreground sm:flex">
    <span className="flex items-center gap-1"><span className={`h-2 w-2 ${TYPE_STYLE.vast_wekelijks.mark}`} /> Vast</span>
    <span className="flex items-center gap-1"><span className={`h-2 w-2 ${TYPE_STYLE.eenmalig.mark}`} /> Afspraak</span>
    <span className="flex items-center gap-1"><span className={`h-2 w-2 ${TYPE_STYLE.onderhoud.mark}`} /> Onderhoud</span>
    {showKeuring && <span className="flex items-center gap-1"><span className={`h-2 w-2 ${TYPE_STYLE.keuring.mark}`} /> Keuring</span>}
  </div>
);
